/**
 * Confirmation dialog for an operator-initiated resource deletion.
 *
 * For a VM it asks the two things the backend requires (AdminDeleteResourceCommandHandler): a
 * reason, and the VM's name typed back exactly. An operator deletes across tenants and is one row
 * away from the wrong customer's VM, so the typed name is what proves the delete landed where it
 * was aimed. Other resource types keep the plain confirm they had.
 *
 * It also asks WHAT to delete. The ordinary answer destroys the machine and removes the record
 * together; the other removes the record alone and leaves the machine running. That second answer
 * exists because the first one cannot always be given: a machine the provider has already lost, or
 * one it refuses to destroy, leaves a row nothing can clear. It is deliberately not the default,
 * and it states what it leaves behind, because choosing it by accident means somebody's VM running
 * on the hypervisor that WiCloud has stopped billing and stopped tracking.
 *
 * The backend checks the machine at n8n before the ordinary deletion and refuses it with
 * VM_NOT_FOUND_ON_PROVIDER when n8n does not know the uuid, so only the record can go. Given the
 * failed request's `error`, the dialog switches to record-only and says why, keeping what was typed —
 * one more click clears the row. Record-only itself asks n8n nothing: an operator may remove the
 * record of a machine on purpose and keep it running.
 *
 * A running or suspended VM is not a dead end on the full deletion: a one-line stepper shows what
 * is in the way (resume, shut down, then delete) and the footer runs the next step through the
 * caller's power action, when the caller passes one (SuperAdmin only). Everything else in the
 * dialog is held until the new status has arrived — see `usePrepStepRunner`.
 */

import { useEffect, useId, useRef, useState } from "react";
import {
  Check,
  Copy,
  ExternalLink,
  HelpCircle,
  Play,
  Power,
} from "lucide-react";
import { toast } from "sonner";
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogBody,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Spinner } from "@/components/ui/spinner";
import { Textarea } from "@/components/ui/textarea";
import {
  DELETE_REASON_MAX_LENGTH,
  DELETE_REASON_MIN_LENGTH,
  RESTORE_WINDOW_HOURS,
  type DeleteResourcePayload,
} from "@/types/admin";
import {
  hasProviderVm,
  isRestorableDeletion as isRestorable,
} from "@/lib/providerVm";
import { APIError } from "@/lib/api";

/**
 * What the operator is deleting. "provider-and-record" is the ordinary deletion and the default;
 * "record-only" drops WiCloud data alone and leaves the machine running at the infrastructure.
 */
type DeleteScope = "provider-and-record" | "record-only";

/** The backend's scope refusal, and the answer it points the operator to. */
const SCOPE_REFUSALS: Record<string, DeleteScope> = {
  VM_NOT_FOUND_ON_PROVIDER: "record-only",
};

/** A scope refusal read off a failed delete, with the backend's own sentence. */
interface ScopeRefusal {
  code: string;
  message: string;
}

interface DeleteResourceDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Name shown in the prompt and, for a VM, the string that has to be typed back. */
  resourceName: string;
  /** Backend ResourceType; only "VirtualMachine" needs the confirmation fields. */
  resourceType: string;
  /**
   * Current status. A running VM must be stopped first, and a suspended one resumed — the provider
   * holds a lock on a suspended machine and refuses to delete it.
   */
  resourceStatus: string;
  /**
   * The VM's provider UUID, which decides whether the suspended block applies at all: with no
   * machine at the provider there is no lock to clear, and the deletion never calls it.
   */
  providerResourceId?: string | null;
  /** Owning tenant, shown so an operator can tell two similarly named VMs apart. */
  tenantName?: string;
  isPending: boolean;
  /**
   * Always called with a payload now, including for non-VM types: `deleteFromProvider` has to travel
   * on every request, and it is the one field no resource type can default its way out of.
   */
  onConfirm: (payload: DeleteResourcePayload) => void;
  /**
   * The last failed delete's error (the mutation's `error`). Read only on the ordinary deletion, for
   * the two scope refusals above; every other failure is the caller's toast.
   */
  error?: unknown;
  /**
   * Shuts the VM down without leaving the dialog, offered when that is the next step. Pass the power
   * mutation's `mutateAsync`: the dialog waits on it, and a rejection is how it learns the step
   * failed. Omit it for an operator who may not run power actions — the step then has no button.
   */
  onShutDown?: () => Promise<unknown>;
  /** Resumes a suspended VM without leaving the dialog, offered when that is the next step. */
  onResume?: () => Promise<unknown>;
  /** A power action on this VM is in flight, started here or anywhere else on the page. */
  isPowerActionPending?: boolean;
}

/** What has to happen to the VM before the full deletion can run, in order. */
type PrepStep = "resume" | "shutdown";

const PREP_STEP_COPY: Record<
  PrepStep,
  { label: string; button: string; running: string }
> = {
  resume: { label: "Resume", button: "Resume", running: "Resuming..." },
  shutdown: {
    label: "Shut down",
    button: "Shut down",
    running: "Shutting down...",
  },
};

/**
 * Longest wait, after a step's request succeeded, for the new status to reach the dialog. The power
 * mutation refetches the row before it resolves, so this only bounds a refetch that failed.
 */
const STATUS_WAIT_MS = 15_000;

/**
 * Runs one preparation step at a time and holds the dialog still until it has landed.
 *
 * Three races it closes: a double click sending the shutdown twice (the ref is set in the same
 * tick as the click; `isPending` only arrives with the next render), the gap between the request
 * succeeding and the new status arriving, in which the button would come back and send a shutdown
 * to a VM already stopped, and the dialog being closed mid-step. The lock lifts when the status
 * moves, when the request fails, or after STATUS_WAIT_MS. Same mechanism as the client console's
 * DeleteVMDialog.
 */
function usePrepStepRunner(open: boolean, status: string) {
  const [inFlight, setInFlight] = useState<PrepStep | null>(null);
  const lockedRef = useRef(false);
  const statusAtStartRef = useRef(status);
  // Each run gets a number, so a stale timer from an earlier step never lifts a later step's lock.
  const runIdRef = useRef(0);
  const timerRef = useRef<number | undefined>(undefined);

  const release = () => {
    lockedRef.current = false;
    window.clearTimeout(timerRef.current);
    setInFlight(null);
  };

  useEffect(() => {
    if (open) {
      release();
    }
  }, [open]);

  useEffect(() => {
    if (lockedRef.current && status !== statusAtStartRef.current) {
      release();
    }
  }, [status]);

  useEffect(() => () => window.clearTimeout(timerRef.current), []);

  const run = async (step: PrepStep, action?: () => Promise<unknown>) => {
    if (!action || lockedRef.current) {
      return;
    }

    lockedRef.current = true;
    statusAtStartRef.current = status;
    runIdRef.current += 1;
    const runId = runIdRef.current;
    setInFlight(step);

    try {
      await action();
    } catch {
      // The mutation's own onError has already said why; the step is offered again.
      if (runId === runIdRef.current) {
        release();
      }
      return;
    }

    if (runId === runIdRef.current && lockedRef.current) {
      timerRef.current = window.setTimeout(() => {
        if (runId === runIdRef.current) {
          release();
        }
      }, STATUS_WAIT_MS);
    }
  };

  return { inFlight, run };
}

export function DeleteResourceDialog({
  open,
  onOpenChange,
  resourceName,
  resourceType,
  resourceStatus,
  providerResourceId,
  tenantName,
  isPending,
  onConfirm,
  error,
  onShutDown,
  onResume,
  isPowerActionPending = false,
}: DeleteResourceDialogProps) {
  const reasonFieldId = useId();
  const nameFieldId = useId();
  const scopeFieldId = useId();

  const [reason, setReason] = useState("");
  const [typedName, setTypedName] = useState("");
  // Which of the two deletions this is. Always reset to the full one on open: the record-only
  // branch leaves a machine behind, and inheriting it from a previous dialog would do that without
  // the operator choosing it for this resource.
  const [scope, setScope] = useState<DeleteScope>("provider-and-record");
  const [scopeRefusal, setScopeRefusal] = useState<ScopeRefusal | null>(null);
  // Set by this dialog's own submit, so an `error` left over from an earlier request (another row)
  // is never read as the answer to this one.
  const submittedRef = useRef(false);

  // A failed delete leaves the dialog open with what was typed still in it; a fresh open starts
  // empty so the previous resource's answers can never be submitted against another one.
  useEffect(() => {
    if (open) {
      setReason("");
      setTypedName("");
      setScope("provider-and-record");
      setScopeRefusal(null);
      submittedRef.current = false;
    }
  }, [open]);

  // Runs once per failed request: a new attempt clears the mutation's error, and its failure is a
  // new object.
  useEffect(() => {
    if (!submittedRef.current || !(error instanceof APIError)) {
      return;
    }

    const suggested = SCOPE_REFUSALS[error.code];
    if (suggested === undefined) {
      return;
    }

    setScope(suggested);
    setScopeRefusal({ code: error.code, message: error.message });
  }, [error]);

  // n8n has no such machine, so the record-only answer leaves nothing running — the "keeps running"
  // warning below would be false.
  const machineUnknownToProvider =
    scopeRefusal?.code === "VM_NOT_FOUND_ON_PROVIDER";
  const isVm = resourceType === "VirtualMachine";
  // What to call the thing in the copy. Every other type reaching this dialog is storage, a network
  // or a bucket, and "resource" covers all three without naming the wrong one.
  const noun = isVm ? "VM" : "resource";

  // The backend refuses a delete while the guest is up (RESOURCE_RUNNING), and refuses a suspended
  // VM because the provider has it locked and will not destroy it (RESOURCE_SUSPENDED). The dialog
  // says what to do instead of collecting answers it cannot send.
  const isRunning =
    resourceType === "VirtualMachine" && resourceStatus === "Running";

  // Conditional on there being a machine at the provider, and the condition is the whole point:
  // AdminDeleteResourceCommandHandler only raises RESOURCE_SUSPENDED when `HasProviderVm()` holds.
  // A row with no uuid — or a SIMULATED- one — has nothing there to be locked and its deletion
  // never calls the provider, so blocking it here would be a dead end no resume could clear, on
  // exactly the broken rows an operator is most likely to be cleaning up. Same reading as the
  // client console's delete dialog.
  const isSuspended =
    resourceType === "VirtualMachine" &&
    resourceStatus === "Suspended" &&
    hasProviderVm(providerResourceId);

  const recordOnlyChosen = scope === "record-only";

  // Mirrors `Resource.HasProviderVm()`: a VM carrying a real, non-simulated provider id. Only such
  // a row has a machine that can be left behind, and only for such a row does "record only" mean
  // anything at all — a disk or a network is never removed at the provider by WiCloud either way.
  const hasMachineAtProvider = isVm && hasProviderVm(providerResourceId);

  // What the backend will actually do, not what was picked. It runs the ordinary deletion for a row
  // with no machine behind it whatever the answer, because that also cleans up the row's actions,
  // snapshots and metrics and tells the tenant. Derived here as well so the copy, the blocks and
  // the payload all describe the same operation.
  const deleteFromProvider = !recordOnlyChosen || !hasMachineAtProvider;
  const leavesMachineBehind = !deleteFromProvider;

  // Mirrors the `restorable` branch of `AdminDeleteResourceCommandHandler` (the name predates the
  // withdrawal of restore): only a VM with a real machine, on the ordinary deletion, goes Terminating,
  // then Decommissioning once n8n has deleted it, and is kept for the deletion window before it is
  // purged. Everything else (record-only, a non-VM, a VM the provider never had) is gone the moment
  // this request answers.
  const isRestorableDeletion = isRestorable(
    resourceType,
    providerResourceId,
    deleteFromProvider,
  );

  // Only the full deletion can be blocked by these: both refusals come from the provider, and a
  // record-only deletion never calls it. The backend skips the same two checks on that branch.
  const isBlocked = deleteFromProvider && (isRunning || isSuspended);

  // The steps still in the way, derived from the same two checks: a suspended VM is resumed, then
  // shut down; a running one only shut down.
  const pendingSteps: PrepStep[] = !isBlocked
    ? []
    : isSuspended
      ? ["resume", "shutdown"]
      : ["shutdown"];
  const nextStep: PrepStep | undefined = pendingSteps[0];
  const nextStepAction = nextStep === "resume" ? onResume : onShutDown;

  // The steps the dialog opened with, so a finished one stays ticked instead of vanishing.
  const [stepsAtOpen, setStepsAtOpen] = useState<PrepStep[]>([]);
  useEffect(() => {
    if (open) {
      setStepsAtOpen(pendingSteps);
    }
    // Only on open: the status moving while the dialog is up is exactly what the steps track.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);
  const planStillCovers = pendingSteps.every((step) =>
    stepsAtOpen.includes(step),
  );
  const plannedSteps = planStillCovers ? stepsAtOpen : pendingSteps;

  const stepRunner = usePrepStepRunner(open, resourceStatus);
  // Everything else in the dialog waits while the VM is changing state under it.
  const isStepBusy = stepRunner.inFlight !== null || isPowerActionPending;
  const isLocked = isPending || isStepBusy;
  const busyStep = stepRunner.inFlight ?? nextStep;

  // VMs only, as before. The record-only branch needs no clause of its own: it is reached only when
  // there is a machine at the provider, which already means the resource is a VM.
  const needsConfirmation = isVm;
  const trimmedReason = reason.trim();
  const reasonTooShort = trimmedReason.length < DELETE_REASON_MIN_LENGTH;
  // Case-sensitive, like the backend.
  const nameMatches = typedName.trim() === resourceName;
  const canDelete =
    !isLocked &&
    !isBlocked &&
    (!needsConfirmation || (!reasonTooShort && nameMatches));

  const handleConfirm = () => {
    if (!canDelete) {
      return;
    }

    submittedRef.current = true;
    onConfirm({
      // Storage, networks and buckets carry neither field; the backend requires both for a VM only.
      ...(needsConfirmation
        ? { reason: trimmedReason, confirmationName: typedName.trim() }
        : {}),
      // The corrected answer, so the toast and the audit row describe one operation. The backend
      // derives it again from the row itself, which is the authority.
      deleteFromProvider,
    });
  };

  return (
    <AlertDialog
      open={open}
      onOpenChange={(next) => {
        // Cancel is disabled while a request or a step runs, so closing must be blocked too.
        if (isLocked && !next) {
          return;
        }

        onOpenChange(next);
      }}
    >
      <AlertDialogContent
        onEscapeKeyDown={(event) => {
          if (isLocked) {
            event.preventDefault();
          }
        }}
      >
        <AlertDialogHeader>
          <AlertDialogTitle>Delete this resource?</AlertDialogTitle>
        </AlertDialogHeader>

        <AlertDialogBody className="space-y-4">
          <AlertDialogDescription className="break-words">
            {`"${resourceName}"`}
            {tenantName ? ` (${tenantName})` : ""}
            {/* Three different sentences rather than one hedged one: the ordinary deletion of a VM
                with a real machine is restorable, the ordinary deletion of anything else destroys
                data outright, and the record-only branch destroys nothing at all. Saying "all data
                will be lost" over the restorable case would be false for the next hour, and
                saying neither of the other two would hide the one fact that makes each of them what
                it is. */}
            {deleteFromProvider
              ? isRestorableDeletion
                ? ` moves to the Deletion filter's "Pending purge (≤${RESTORE_WINDOW_HOURS}h)" view, kept for ${RESTORE_WINDOW_HOURS}h, then deleted for good.` +
                  " Firewall rules and snapshots are removed right away."
                : ` will be permanently deleted. This cannot be undone${
                    isVm ? ", and all data on this VM will be lost." : "."
                  }`
              : " will be removed from WiCloud. This cannot be undone."}
          </AlertDialogDescription>
          {/* Asked before anything else, because the two notes below only apply to one of the
            answers and the fields below are collected for both. */}
          <div className="space-y-2">
            <p className="text-sm font-medium">What should be deleted?</p>
            <RadioGroup
              aria-label="What should be deleted?"
              value={scope}
              onValueChange={(next) => setScope(next as DeleteScope)}
              disabled={isLocked}
              className="gap-3"
            >
              <label
                htmlFor={`${scopeFieldId}-both`}
                className="flex cursor-pointer items-start gap-3"
              >
                <RadioGroupItem
                  value="provider-and-record"
                  id={`${scopeFieldId}-both`}
                  className="mt-0.5 cursor-pointer"
                />
                <span className="text-sm leading-snug">
                  <span className="font-medium">
                    {`The ${noun} and its WiCloud record`}
                  </span>
                  <span className="block text-muted-foreground">
                    {`The ${noun} is destroyed at the infrastructure and the record is removed. This is the ordinary deletion.`}
                  </span>
                </span>
              </label>
              <label
                htmlFor={`${scopeFieldId}-record`}
                className="flex cursor-pointer items-start gap-3"
              >
                <RadioGroupItem
                  value="record-only"
                  id={`${scopeFieldId}-record`}
                  className="mt-0.5 cursor-pointer"
                />
                <span className="text-sm leading-snug">
                  <span className="font-medium">The WiCloud record only</span>
                  <span className="block text-muted-foreground">
                    {`Nothing is asked of the infrastructure. Use this when the ${noun} is already gone from the provider, or when the provider will not delete it.`}
                  </span>
                </span>
              </label>
            </RadioGroup>
          </div>

          {/* The whole reason this answer is not the default. Two different facts, and quoting the
            wrong one is worse than saying nothing: with a machine at the provider it is left
            running and someone has to go and remove it, and the uuid is the only handle they will
            have once the record is gone. With no machine there, nothing is left behind at all —
            which is exactly the case this option was added for. */}
          {/* Why the answer above changed. Kept up after a manual switch too: it is still the
            backend's last word on this machine. */}
          {scopeRefusal && (
            <div
              role="alert"
              className="space-y-1 rounded-md border border-warn/40 bg-warn-soft p-3 text-sm"
            >
              <p className="font-medium text-warn-ink">
                {scopeRefusal.message}
              </p>
              <p className="text-foreground">
                {machineUnknownToProvider
                  ? `The infrastructure has no machine with this ${noun}'s uuid, so there is nothing there to delete. "The WiCloud record only" is now selected — confirm to clear this row.`
                  : `The machine is still at the infrastructure, so removing only the record would leave it running untracked. "The ${noun} and its WiCloud record" is now selected — confirm to delete it normally.`}
              </p>
            </div>
          )}

          {recordOnlyChosen &&
            !machineUnknownToProvider &&
            (leavesMachineBehind ? (
              <div className="space-y-2 rounded-md border border-destructive/40 bg-destructive/5 p-3 text-sm">
                <p className="font-medium text-destructive-ink">
                  {`The ${noun} will keep running at the infrastructure.`}
                </p>
                <p className="text-foreground">
                  WiCloud stops billing it and nothing here points at it any
                  more, so it has to be removed by hand. Copy the machine ID
                  first — it is the only handle left once the record is gone.
                </p>
                {/* A button, not just selectable text: the sentence above tells the operator to copy
                  this, and it is about to become the only way to find the machine again. */}
                <div className="flex items-start gap-2">
                  <p className="break-all font-mono text-xs text-foreground">
                    {providerResourceId}
                  </p>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="h-6 w-6 shrink-0"
                    aria-label="Copy machine ID"
                    onClick={() => {
                      navigator.clipboard.writeText(providerResourceId ?? "");
                      toast.success("Machine ID copied");
                    }}
                  >
                    <Copy className="h-3.5 w-3.5" />
                  </Button>
                </div>
              </div>
            ) : (
              <p className="rounded-md border border-border bg-muted/40 p-3 text-sm text-foreground">
                {`This ${noun} has no machine recorded at the infrastructure, so there is nothing there to leave behind — the two answers come to the same thing. The ordinary deletion runs, which also clears this ${noun}'s history and tells the customer.`}
              </p>
            ))}

          {plannedSteps.length > 0 && deleteFromProvider && (
            <div className="space-y-2">
              <p className="text-sm text-foreground">
                {isSuspended
                  ? "This VM is suspended, so the provider has it locked. Resume it, then shut it down."
                  : isRunning
                    ? "This VM is running. Shut it down before deleting it."
                    : "The VM is stopped and can be deleted now."}
              </p>
              <DeleteStepper steps={plannedSteps} pendingSteps={pendingSteps} />
            </div>
          )}

          {needsConfirmation && !isBlocked && (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor={reasonFieldId}>
                  Reason for deletion{" "}
                  <span className="text-destructive-ink" aria-hidden="true">
                    *
                  </span>
                </Label>
                <Textarea
                  id={reasonFieldId}
                  required
                  value={reason}
                  onChange={(event) => setReason(event.target.value)}
                  placeholder={`Why is this ${noun} being deleted?`}
                  maxLength={DELETE_REASON_MAX_LENGTH}
                  rows={3}
                  disabled={isLocked}
                  aria-invalid={reason.length > 0 && reasonTooShort}
                />
                <p className="text-xs text-muted-foreground">
                  {`At least ${DELETE_REASON_MIN_LENGTH} characters, at most ${DELETE_REASON_MAX_LENGTH}. Recorded in the audit log.`}
                </p>
              </div>

              <div className="space-y-1.5">
                {/* Same shape as the client's DeleteVMDialog: the name sits in the label (break-all,
                  so a long name wraps), its copy button beside it. */}
                <div className="flex items-center gap-1">
                  <Label htmlFor={nameFieldId} className="min-w-0 break-all">
                    Type{" "}
                    <code className="rounded bg-panel-2 px-1 py-0.5 font-mono text-[0.8125rem] text-foreground">
                      {resourceName}
                    </code>{" "}
                    to confirm
                  </Label>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="h-6 w-6 shrink-0 text-muted-foreground"
                    onClick={() => {
                      void navigator.clipboard.writeText(resourceName);
                      toast.success(
                        `${noun[0].toUpperCase()}${noun.slice(1)} name copied`,
                      );
                    }}
                    title={`Copy the ${noun} name`}
                    aria-label={`Copy the ${noun} name`}
                    disabled={isLocked}
                  >
                    <Copy className="h-3.5 w-3.5" aria-hidden />
                  </Button>
                </div>
                <Input
                  id={nameFieldId}
                  className="font-mono"
                  value={typedName}
                  onChange={(event) => setTypedName(event.target.value)}
                  autoComplete="off"
                  autoCorrect="off"
                  spellCheck={false}
                  disabled={isLocked}
                  aria-invalid={typedName.length > 0 && !nameMatches}
                  onKeyDown={(event) => {
                    if (event.key === "Enter") {
                      event.preventDefault();
                      handleConfirm();
                    }
                  }}
                />
                {typedName.length > 0 && !nameMatches && (
                  <p className="text-xs text-destructive">
                    {`The name you typed does not match the ${noun} name.`}
                  </p>
                )}
              </div>
            </div>
          )}
        </AlertDialogBody>

        <AlertDialogFooter>
          {/* A VM's deletion only: that is the one with a job, retries and a deletion window behind
              it. A popover, so reading it never closes a dialog the operator is half-way through. */}
          {isVm && <HelpPopover />}
          <AlertDialogCancel disabled={isLocked}>Cancel</AlertDialogCancel>
          {/* The next step sits beside Delete, so the operator's hand never moves. */}
          {deleteFromProvider && nextStep !== undefined && nextStepAction && (
            <Button
              variant="outline"
              className="gap-1.5"
              onClick={() => void stepRunner.run(nextStep, nextStepAction)}
              disabled={isStepBusy}
            >
              {isStepBusy ? (
                <Spinner />
              ) : nextStep === "resume" ? (
                <Play className="h-4 w-4" aria-hidden />
              ) : (
                <Power className="h-4 w-4" aria-hidden />
              )}
              {isStepBusy && busyStep
                ? PREP_STEP_COPY[busyStep].running
                : PREP_STEP_COPY[nextStep].button}
            </Button>
          )}
          {/* A plain Button, not AlertDialogAction: the action closes the dialog on click, and
              this one must stay open while the request is in flight and when it fails. */}
          <Button
            variant="destructive"
            onClick={handleConfirm}
            disabled={!canDelete}
          >
            {isPending && <Spinner className="mr-2" />}
            {/* The caption names the answer that is selected. "Delete" over a record-only
                deletion would promise the machine goes too. */}
            {isPending
              ? deleteFromProvider
                ? "Deleting..."
                : "Removing record..."
              : deleteFromProvider
                ? "Delete"
                : "Remove record"}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

/**
 * What happens after "Delete" on a VM, opened in place. The long version, with every refusal and
 * audit row, is `/docs/vm-delete.html`.
 */
function HelpPopover() {
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="sm" className="sm:mr-auto">
          <HelpCircle className="mr-2 h-4 w-4" />
          How this works
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        side="top"
        className="max-h-[70vh] w-96 space-y-3 overflow-y-auto text-sm"
      >
        <p className="text-muted-foreground">
          For a VM with a machine at the infrastructure. Nothing is removed from
          WiCloud's database; the row only changes status.
        </p>

        <div className="space-y-1.5">
          <p className="font-medium">After Delete</p>
          <ul className="space-y-1 text-muted-foreground">
            <li>
              · the VM, its disks and networks stay in the list as{" "}
              <b>Deleting</b> until n8n confirms
            </li>
            <li>
              · then they move to the Deletion filter's <b>Pending purge</b>{" "}
              view for {RESTORE_WINDOW_HOURS}h, with the same uuid and IP
            </li>
            <li>· after that window they are deleted for good</li>
          </ul>
        </div>

        <div className="space-y-1.5">
          <p className="font-medium">If n8n does not confirm</p>
          <ul className="space-y-1 text-muted-foreground">
            <li>· retried after 1, 3 and 5 minutes — four tries in all</li>
            <li>
              · before counting a refusal, WiCloud asks n8n whether it still has
              the VM; if it does not, the deletion is done
            </li>
            <li>
              · after the fourth refusal the VM goes back to the status it had,
              with a "last deletion did not complete" banner
            </li>
          </ul>
        </div>

        <div className="space-y-1.5">
          <p className="font-medium">Refused before anything changes</p>
          <ul className="space-y-1 text-muted-foreground">
            <li>· running (stop it), suspended (resume, then stop it)</li>
            <li>
              · n8n does not know the VM — delete the WiCloud record only — or
              does not answer
            </li>
            <li>
              · a renewal transfer is awaiting verification — settle it first
            </li>
          </ul>
        </div>

        <p className="text-muted-foreground">
          An unpaid renewal order stays open but cannot be paid while the VM is
          deleted.
        </p>

        <Button variant="outline" size="sm" asChild className="w-full">
          <a href="/docs/vm-delete.html" target="_blank" rel="noreferrer">
            Full guide
            <ExternalLink className="ml-2 h-3.5 w-3.5" />
          </a>
        </Button>
      </PopoverContent>
    </Popover>
  );
}

/**
 * One line of numbered steps ending on Delete: a tick once a step is done, the next one in amber,
 * the rest muted. Same shape as the client console's delete stepper.
 */
function DeleteStepper({
  steps,
  pendingSteps,
}: {
  steps: PrepStep[];
  pendingSteps: PrepStep[];
}) {
  const all: (PrepStep | "delete")[] = [...steps, "delete"];
  const current = pendingSteps[0] ?? "delete";

  return (
    <ol className="flex items-center gap-1.5 text-xs">
      {all.map((step, index) => {
        const done = step !== "delete" && !pendingSteps.includes(step);
        const isCurrent = step === current;

        return (
          // Every step after the first carries the line leading into it, so the steps spread
          // evenly and the list holds only real steps for a screen reader.
          <li
            key={step}
            className={
              index > 0
                ? "flex min-w-0 flex-1 items-center gap-1.5"
                : "flex shrink-0 items-center gap-1.5"
            }
            aria-current={isCurrent ? "step" : undefined}
          >
            {index > 0 && (
              <span
                aria-hidden
                className="mr-0.5 h-px min-w-3 flex-1 bg-hairline"
              />
            )}
            <span
              className={
                "flex h-5 w-5 shrink-0 items-center justify-center rounded-full font-semibold tabular-nums " +
                (done
                  ? "bg-ok-soft text-ok"
                  : isCurrent
                    ? "bg-warn-soft text-warn"
                    : "bg-panel-2 text-muted-foreground")
              }
            >
              {done ? (
                <Check className="h-3 w-3" aria-label="Done" />
              ) : (
                index + 1
              )}
            </span>
            <span
              className={
                isCurrent
                  ? "shrink-0 whitespace-nowrap font-medium text-foreground"
                  : "shrink-0 whitespace-nowrap text-muted-foreground"
              }
            >
              {step === "delete" ? "Delete" : PREP_STEP_COPY[step].label}
            </span>
          </li>
        );
      })}
    </ol>
  );
}
